import { useEffect, useRef, useState } from 'react'
import { SelectBox } from '@/shared/ui/SelectBox'
import { SelectList } from '@/shared/ui/SelectList'

export type DropdownOption = { value: string; label: string }

export function Dropdown({
  options,
  value,
  placeholder,
  onChange,
  size = 'default',
  className = '',
  ariaLabel,
}: {
  options: DropdownOption[]
  value: string | null
  placeholder: string
  onChange: (value: string) => void
  size?: 'default' | 'small' | 'pc'
  className?: string
  ariaLabel?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  const selected = options.find((o) => o.value === value) ?? null

  return (
    <div ref={rootRef} className="relative">
      <SelectBox
        label={selected === null ? placeholder : selected.label}
        size={size}
        isOpen={isOpen}
        className={`w-full ${className}`}
        onClick={() => setIsOpen((v) => !v)}
      />

      {isOpen && (
        <div
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-50 max-h-60 overflow-y-auto rounded-lg border border-white-300 bg-white-50 p-1 shadow-[0_4px_16px_rgb(0_0_0/10%)]"
          role="listbox"
          {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        >
          {options.map((o) => (
            <SelectList
              key={o.value}
              label={o.label}
              size={size === 'small' ? 'small' : 'medium'}
              isSelected={o.value === value}
              onSelect={() => {
                onChange(o.value)
                setIsOpen(false)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
